import { useState, useEffect, useCallback } from 'react';
import { Book } from '../types';
import { supabase } from '../lib/supabaseClient';
import { getPublicStorageUrl } from '../utils/storage';
import { toCamelCase } from '../utils/caseConversion';

export const useStories = () => {
  const [stories, setStories] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Chargement des histoires depuis Supabase...');

      // RLS policy automatically filters based on user's subscription_tier
      // - Non-authenticated or free users: only visible + non-premium stories
      // - Premium users: all visible stories (free + premium)
      // - Admins: all stories
      const { data: storiesData, error: storiesError } = await supabase
        .from('stories')
        .select('*')
        .eq('visible', true)
        .order('created_at', { ascending: false });

        if (storiesError) {
          throw new Error(`Erreur Supabase: ${storiesError.message}`);
        }

        // Convert snake_case from database to camelCase for JavaScript
        const storiesDataCamelCase = toCamelCase(storiesData);

        // Transform Supabase format to Book interface (pour compatibilité avec l'existant)
        const transformedStories: Book[] = (storiesDataCamelCase || []).map((story: any) => {
          // Convertir les pages de l'API (objet) vers le format attendu (array)
          const storyPages = story.pages || {};
          const pageKeys = Object.keys(storyPages);
          
          const pagesArray = pageKeys.map((pageKey, index) => {
            const page = storyPages[pageKey];

            // Extraire le contenu depuis la nouvelle structure admin-frontoffice
            let pageContent = '';
            if (page.content?.elements) {
              // Nouvelle structure: récupérer le texte depuis les éléments
              const textElements = page.content.elements.filter((el: any) => el.type === 'text');
              pageContent = textElements.map((el: any) => el.content?.fr || '').join('\n');
            } else if (page.content?.text?.fr) {
              // Ancienne structure hardcodée
              pageContent = page.content.text.fr;
            }

            // Gérer les choix ET la navigation automatique
            // Note: Maintenant en camelCase grâce à toCamelCase()
            let pageChoices: any[] = [];
            let hasAutoNavigation = false;

            if (page.choices && page.choices.length > 0) {
              // Mode choix multiples
              pageChoices = page.choices.map((choice: any, choiceIndex: number) => {
                const nextPageIndex = pageKeys.indexOf(choice.nextPage); // déjà en camelCase
                return {
                  id: choiceIndex + 1,
                  text: choice.text?.fr || '',
                  nextPage: nextPageIndex >= 0 ? nextPageIndex : 0,
                  emoji: '👆'
                };
              });
            } else if (page.nextPage) { // déjà en camelCase
              // Mode navigation automatique - créer un choix "Continuer"
              const nextPageIndex = pageKeys.indexOf(page.nextPage);
              if (nextPageIndex >= 0) {
                pageChoices = [{
                  id: 1,
                  text: 'Continuer',
                  nextPage: nextPageIndex,
                  emoji: '➡️'
                }];
                hasAutoNavigation = true;
              }
            }

            return {
              id: index + 1,
              content: pageContent,
              choices: pageChoices,
              animation: 'fadeIn',
              backgroundSound: '',
              image: getPublicStorageUrl(page.content?.backgroundImage) || '',
              // Métadonnées pour la navigation
              isAutoNavigation: hasAutoNavigation,
              originalPageKey: pageKey
            };
          });

          return {
            id: story.id,
            title: story.translations.fr.title, // Default to French for compatibility
            category: story.translations.fr.category,
            pages: story.longestPathPages || pagesArray.length, // Maintenant en camelCase
            cover: getPublicStorageUrl(story.coverImage) || "📖", // Maintenant en camelCase
            premium: story.premium,
            objects: story.possibleRewards || [], // Maintenant en camelCase
            content: pagesArray, // Pages converties pour le ReadingScreen
            // Données complètes pour les composants avancés
            fullData: {
              translations: story.translations,
              pages: story.pages,
              startPage: story.startPage, // Maintenant en camelCase
              tags: story.tags || [],
              orientation: story.orientation
            }
          };
        });

        console.log(`✅ ${transformedStories.length} histoires chargées depuis Supabase`);
        setStories(transformedStories);
        
      } catch (err) {
        console.error('❌ Erreur lors du chargement des histoires:', err);
        setError(err instanceof Error ? err.message : 'Erreur inconnue');
        
        // Fallback to empty array
        setStories([]);
      } finally {
        setLoading(false);
      }
    }, []);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  return {
    stories,
    loading,
    error,
    refetch: fetchStories
  };
};

export default useStories;